import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import { formatViews, heatLabel } from "@/utils/format";
import { topics } from "@/mocks/topics";

interface TopicAsideProps {
  topic: Topic;
}

export default function TopicAside({ topic }: TopicAsideProps) {
  const related = topics
    .filter((item) => item.id !== topic.id && item.category === topic.category)
    .slice(0, 3);
  const fallback = topics.filter((item) => item.id !== topic.id).slice(0, 3);
  const recommended = related.length > 0 ? related : fallback;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-card border border-background-200 bg-background-100 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
          <span className="flex h-4 w-4 items-center justify-center text-primary-600">
            <i className="ri-fire-line text-base" />
          </span>
          话题热度
        </h2>
        <div className="mt-4 flex items-end justify-between">
          <span className="font-heading text-3xl font-bold text-primary-600">{topic.heat}</span>
          <span className="rounded-md bg-primary-100 px-2 py-1 text-xs font-medium text-primary-700">
            {heatLabel(topic.heat)} · 100 分制
          </span>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background-200">
          <div
            className="h-full rounded-full bg-primary-500"
            style={{ width: `${topic.heat}%` }}
          />
        </div>
        <dl className="mt-5 grid grid-cols-3 gap-3 text-center">
          <div className="rounded-lg bg-background-50 py-3">
            <dt className="text-[11px] text-foreground-500">阅读</dt>
            <dd className="mt-1 text-sm font-semibold text-foreground-900">
              {formatViews(topic.views)}
            </dd>
          </div>
          <div className="rounded-lg bg-background-50 py-3">
            <dt className="text-[11px] text-foreground-500">讨论</dt>
            <dd className="mt-1 text-sm font-semibold text-foreground-900">{topic.comments}</dd>
          </div>
          <div className="rounded-lg bg-background-50 py-3">
            <dt className="text-[11px] text-foreground-500">阅读时长</dt>
            <dd className="mt-1 text-sm font-semibold text-foreground-900">
              {topic.readMinutes} 分钟
            </dd>
          </div>
        </dl>
      </div>

      <div className="rounded-card border border-background-200 bg-background-100 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
          <span className="flex h-4 w-4 items-center justify-center text-accent-600">
            <i className="ri-price-tag-3-line text-base" />
          </span>
          话题标签
        </h2>
        <ul className="mt-4 flex flex-wrap gap-2">
          {topic.tags.map((tag) => (
            <li
              key={tag}
              className="rounded-md bg-secondary-100 px-2.5 py-1.5 text-xs text-secondary-900"
            >
              #{tag}
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-card border border-background-200 bg-background-100 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
          <span className="flex h-4 w-4 items-center justify-center text-accent-600">
            <i className="ri-links-line text-base" />
          </span>
          相关话题推荐
        </h2>
        <ul className="mt-4 flex flex-col gap-4">
          {recommended.map((item) => (
            <li key={item.id} className="flex gap-3">
              <Link
                to={`/topic/${item.id}`}
                className="h-[60px] w-[80px] shrink-0 overflow-hidden rounded-lg bg-background-200"
              >
                <img
                  src={item.cover}
                  alt={item.coverAlt}
                  className="h-full w-full object-cover object-top"
                />
              </Link>
              <div className="min-w-0">
                <h3 className="line-clamp-2 text-sm leading-snug text-foreground-800">
                  <Link to={`/topic/${item.id}`} className="transition-colors hover:text-primary-600">
                    {item.title}
                  </Link>
                </h3>
                <p className="mt-1 text-[11px] text-foreground-500">{item.categoryName}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}